import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { useExperienceStore } from '../store/experienceStore'
import { QUALITY_PRESETS } from '../config/quality'

/** Soft disc sprite for filmic dust / energy (not square points) */
function makeSpriteTexture(): THREE.Texture {
  const size = 128
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')!
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2)
  g.addColorStop(0, 'rgba(255,255,255,1)')
  g.addColorStop(0.25, 'rgba(220,240,255,0.85)')
  g.addColorStop(0.55, 'rgba(140,190,255,0.25)')
  g.addColorStop(1, 'rgba(0,0,0,0)')
  ctx.fillStyle = g
  ctx.fillRect(0, 0, size, size)
  const tex = new THREE.CanvasTexture(canvas)
  tex.colorSpace = THREE.SRGBColorSpace
  return tex
}

export function ParticleField() {
  const pointsRef = useRef<THREE.Points>(null)
  const quality = useExperienceStore((s) => s.quality)
  const count = QUALITY_PRESETS[quality].particles
  const sprite = useMemo(() => makeSpriteTexture(), [])

  const { positions, velocities, phases, colors } = useMemo(() => {
    const positions = new Float32Array(count * 3)
    const velocities = new Float32Array(count * 3)
    const phases = new Float32Array(count)
    const colors = new Float32Array(count * 3)
    for (let i = 0; i < count; i++) {
      const i3 = i * 3
      // concentrate mid-band around chair / person height for immersion
      const r = 3 + Math.random() * 28
      const theta = Math.random() * Math.PI * 2
      const y = (Math.random() - 0.45) * 14
      positions[i3] = Math.cos(theta) * r
      positions[i3 + 1] = y
      positions[i3 + 2] = Math.sin(theta) * r * 0.85
      velocities[i3] = (Math.random() - 0.5) * 0.35
      velocities[i3 + 1] = (Math.random() - 0.5) * 0.12
      velocities[i3 + 2] = (Math.random() - 0.5) * 0.35
      phases[i] = Math.random() * Math.PI * 2
      // warm dust + cool energy mix
      const cool = Math.random()
      if (cool > 0.55) {
        colors[i3] = 0.55 + Math.random() * 0.45
        colors[i3 + 1] = 0.75 + Math.random() * 0.25
        colors[i3 + 2] = 1.0
      } else {
        colors[i3] = 1.0
        colors[i3 + 1] = 0.75 + Math.random() * 0.2
        colors[i3 + 2] = 0.4 + Math.random() * 0.3
      }
    }
    return { positions, velocities, phases, colors }
  }, [count])

  useFrame((state, dt) => {
    const pts = pointsRef.current
    if (!pts) return
    const { timeSpeed, traverseIntensity, palmNdc } =
      useExperienceStore.getState()
    const pos = pts.geometry.attributes.position.array as Float32Array
    const speed = Math.abs(timeSpeed)
    const dir = Math.sign(timeSpeed) || 1
    const flow = 0.4 + speed * 14 + traverseIntensity * 5
    const t = state.clock.elapsedTime

    const attract = palmNdc
      ? new THREE.Vector3(palmNdc.x * 5, palmNdc.y * 3.2 - 0.2, 1.5)
      : new THREE.Vector3(0, -0.3, 1.8)

    for (let i = 0; i < count; i++) {
      const i3 = i * 3
      const ph = phases[i]
      // orbital swirl + time flow
      const swirl = 0.15 + speed * 0.8
      pos[i3] +=
        velocities[i3] * flow * dt * dir +
        Math.cos(t * 0.4 + ph) * swirl * dt
      pos[i3 + 1] +=
        velocities[i3 + 1] * flow * dt * 0.5 +
        Math.sin(t * 0.7 + ph) * 0.08 * dt
      pos[i3 + 2] +=
        velocities[i3 + 2] * flow * dt * dir +
        Math.sin(t * 0.4 + ph) * swirl * dt

      if (traverseIntensity > 0.04 || speed > 0.05) {
        const pull = 0.015 + traverseIntensity * 0.04 + speed * 0.02
        pos[i3] += (attract.x - pos[i3]) * pull * dt * 8
        pos[i3 + 1] += (attract.y - pos[i3 + 1]) * pull * dt * 8
        pos[i3 + 2] += (attract.z - pos[i3 + 2]) * pull * dt * 5
      }

      const d = Math.hypot(pos[i3], pos[i3 + 1] * 0.6, pos[i3 + 2])
      if (d > 36 || d < 1.2) {
        const r = 4 + Math.random() * 22
        const theta = Math.random() * Math.PI * 2
        pos[i3] = Math.cos(theta) * r
        pos[i3 + 1] = (Math.random() - 0.45) * 12
        pos[i3 + 2] = Math.sin(theta) * r * 0.85
      }
    }
    pts.geometry.attributes.position.needsUpdate = true
    // material size pulses with speed
    const mat = pts.material as THREE.PointsMaterial
    mat.size = 0.12 + speed * 0.35 + traverseIntensity * 0.2
    mat.opacity = 0.35 + Math.min(0.55, speed * 1.2 + traverseIntensity * 0.35)
  })

  return (
    <points ref={pointsRef} frustumCulled={false}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
        <bufferAttribute attach="attributes-color" args={[colors, 3]} />
      </bufferGeometry>
      <pointsMaterial
        map={sprite}
        size={0.18}
        vertexColors
        transparent
        opacity={0.55}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        sizeAttenuation
        alphaTest={0.01}
      />
    </points>
  )
}
